import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1479">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1480">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1481">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1482">
            <span className="w-8 h-px bg-teal" data-id="element-1483"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1484">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1485"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1486">
            Privacy Policy
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1487">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1488">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-1489">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1490">
            At SyNeural, we are committed to protecting your privacy and
            handling your personal information with care and transparency. This
            Privacy Policy explains how we collect, use, store, and protect your
            information when you use our platform, website, and services.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1491">
            By using SyNeural's services, you agree to the practices described
            in this Privacy Policy. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1492">
        {/* Information We Collect */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1493">
          <div className="flex items-center gap-4 mb-6" data-id="element-1494">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1495">
              <Database size={24} data-id="element-1496" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1497">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6" data-id="element-1498">
            <div data-id="element-1499">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1500">
                Personal Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1501">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1502">
                <li data-id="element-1503">Name, email address, and contact information</li>
                <li data-id="element-1504">Organization or institutional affiliation</li>
                <li data-id="element-1505">Account credentials and authentication information</li>
                <li data-id="element-1506">Profile information and preferences</li>
                <li data-id="element-1507">Communication history and support inquiries</li>
              </ul>
            </div>

            <div data-id="element-1508">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1509">
                Assessment and Platform Data
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1510">
                When you use our CJI and NCJI assessment tools, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1511">
                <li data-id="element-1512">Assessment responses and performance data</li>
                <li data-id="element-1513">Reasoning patterns and decision-making metrics</li>
                <li data-id="element-1514">Development pathway selections and progress tracking</li>
                <li data-id="element-1515">
                  Aggregated and anonymized capability data for research
                  purposes
                </li>
              </ul>
            </div>

            <div data-id="element-1516">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1517">
                Technical Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1518">
                We automatically collect certain technical information,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1519">
                <li data-id="element-1520">Device information, browser type, and operating system</li>
                <li data-id="element-1521">IP address and general location data</li>
                <li data-id="element-1522">Usage data, page views, and interaction patterns</li>
                <li data-id="element-1523">Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How We Use Your Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1524">
          <div className="flex items-center gap-4 mb-6" data-id="element-1525">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1526">
              <Eye size={24} data-id="element-1527" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1528">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1529">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1530">
            <li data-id="element-1531">To provide, maintain, and improve our platform and services</li>
            <li data-id="element-1532">
              To deliver personalized assessment results, insights, and
              development recommendations
            </li>
            <li data-id="element-1533">
              To conduct research and analysis to advance our understanding of
              judgment, decision-quality, and human capability
            </li>
            <li data-id="element-1534">
              To communicate with you about your account, updates, and support
            </li>
            <li data-id="element-1535">To ensure platform security and prevent fraud or misuse</li>
            <li data-id="element-1536">
              To comply with legal obligations and enforce our terms of service
            </li>
            <li data-id="element-1537">
              To develop new features, tools, and institutional applications
            </li>
          </ul>
        </motion.div>

        {/* Data Sharing and Disclosure */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1538">
          <div className="flex items-center gap-4 mb-6" data-id="element-1539">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1540">
              <UserCheck size={24} data-id="element-1541" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1542">
              Data Sharing and Disclosure
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1543">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1544">
            <li data-id="element-1545">
              <strong data-id="element-1546">With Your Consent:</strong> When you explicitly authorize
              us to share your information
            </li>
            <li data-id="element-1547">
              <strong data-id="element-1548">Institutional Partners:</strong> If you access SyNeural
              through an educational institution, employer, or government
              program, we may share relevant data with that organization as
              agreed upon
            </li>
            <li data-id="element-1549">
              <strong data-id="element-1550">Service Providers:</strong> With trusted third-party
              vendors who help us operate our platform (e.g., hosting,
              analytics, customer support)
            </li>
            <li data-id="element-1551">
              <strong data-id="element-1552">Research Partners:</strong> Aggregated, anonymized data
              may be shared with research institutions for academic and
              scientific purposes
            </li>
            <li data-id="element-1553">
              <strong data-id="element-1554">Legal Requirements:</strong> When required by law, court
              order, or to protect our rights and safety
            </li>
            <li data-id="element-1555">
              <strong data-id="element-1556">Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </motion.div>

        {/* Data Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1557">
          <div className="flex items-center gap-4 mb-6" data-id="element-1558">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1559">
              <Lock size={24} data-id="element-1560" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1561">
              Data Security
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1562">
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1563">
            <li data-id="element-1564">Encryption of data in transit and at rest</li>
            <li data-id="element-1565">Secure authentication and access controls</li>
            <li data-id="element-1566">Regular security audits and vulnerability assessments</li>
            <li data-id="element-1567">Employee training on data protection and privacy practices</li>
            <li data-id="element-1568">
              Incident response procedures and breach notification protocols
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1569">
            While we take reasonable precautions, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot
            guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </motion.div>

        {/* Your Rights and Choices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1570">
          <div className="flex items-center gap-4 mb-6" data-id="element-1571">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1572">
              <Shield size={24} data-id="element-1573" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1574">
              Your Rights and Choices
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1575">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1576">
            <li data-id="element-1577">
              <strong data-id="element-1578">Access:</strong> Request a copy of the personal
              information we hold about you
            </li>
            <li data-id="element-1579">
              <strong data-id="element-1580">Correction:</strong> Request correction of inaccurate or
              incomplete information
            </li>
            <li data-id="element-1581">
              <strong data-id="element-1582">Deletion:</strong> Request deletion of your personal
              information (subject to legal obligations)
            </li>
            <li data-id="element-1583">
              <strong data-id="element-1584">Portability:</strong> Request a copy of your data in a
              structured, machine-readable format
            </li>
            <li data-id="element-1585">
              <strong data-id="element-1586">Opt-Out:</strong> Opt out of certain data processing
              activities, such as marketing communications
            </li>
            <li data-id="element-1587">
              <strong data-id="element-1588">Withdraw Consent:</strong> Withdraw consent for data
              processing where consent is the legal basis
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1589">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1590">
              privacy@syneural.com
            </a>
            .
          </p>
        </motion.div>

        {/* Data Retention */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1591">
          <div className="flex items-center gap-4 mb-6" data-id="element-1592">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1593">
              <FileText size={24} data-id="element-1594" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1595">
              Data Retention
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1596">
            We retain your personal information for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements. Assessment data may be
            retained longer for research and platform improvement purposes, but
            will be anonymized where possible. You may request deletion of your
            account and associated data at any time, subject to legal retention
            requirements.
          </p>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1597">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1598">
            Children's Privacy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1599">
            SyNeural's services are not directed to children under 13 years of
            age. We do not knowingly collect personal information from children
            under 13. If we become aware that we have collected information from
            a child under 13, we will take steps to delete such information. For
            educational use cases involving minors, we work with schools and
            institutions to ensure appropriate parental consent and data
            protection measures are in place.
          </p>
        </motion.div>

        {/* International Data Transfers */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1600">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1601">
            International Data Transfers
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1602">
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure that such transfers
            comply with applicable data protection laws and implement
            appropriate safeguards, such as standard contractual clauses, to
            protect your information.
          </p>
        </motion.div>

        {/* Changes to This Policy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1603">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1604">
            Changes to This Privacy Policy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1605">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or other
            factors. We will notify you of material changes by posting the
            updated policy on our website and updating the "Last Updated" date.
            Your continued use of our services after such changes constitutes
            acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Contact Us */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1606">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1607">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1608">
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1609">
            <p data-id="element-1610">
              <strong data-id="element-1611">Email:</strong>{' '}
              <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1612">
                privacy@syneural.com
              </a>
            </p>
            <p data-id="element-1613">
              <strong data-id="element-1614">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1615">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}